<template>
	<div ref="wrapperElement" class="form-input form-codearea" :class="[
                {
                'is-active': isActive,
                'is-required': isRequired,
                'has-error': hasError,
                'has-warning': !hasError && props.warning,
                'is-inverted': props.inverted,
                'is-plain': props.plain,
                'is-loading': isLoading,
                'is-disabled': props.disabled,
                'has-suffix': hasSuffix
            }]">

		<div class="form-input__content" :class="{'h-full': isFullScreen}">
			<span v-if="props.label && !isFullScreen" class="form-input__label">{{ props.label }}</span>

			<bubble-menu v-if="editor && !props.disabled" :editor="editor" :should-show="(itProps) => itProps.editor.isActive('link') || itProps.from !== itProps.to"
						 class="form-input-menu-bubble">
				<div v-if="linkMenuVisible" class="flex">
					<btn icon small color="black" @click="linkMenuVisible = false">
						<Icon :icon="icons.back"/>
					</btn>
					<form-input small autofocus hide-details clearable prevent-submit @clear="setLinkUrl(null)"
								class="min-w-300"
								@key-enter.prevent="setLinkUrl(linkUrl)" @key-esc.prevent="hideLinkMenu" v-model="linkUrl" placeholder="https://">
						<template #suffix>
							<Icon :icon="icons.save" class="cursor-pointer" @click="setLinkUrl(linkUrl)">
							</Icon>
						</template>
					</form-input>
				</div>

				<template v-else>
					<btn v-if="editor.isActive('link')" icon small color="black" :value="true" @click="editor.commands.unsetLink()">
						<Icon :icon="icons.unlink"/>
					</btn>
					<btn v-else icon small color="black" :value="editor.isActive('link')" @click="showLinkMenu(editor.getAttributes('link'))">
						<Icon :icon="icons.link"/>
					</btn>

					<btn icon small color="black" :value="editor.isActive('bold')" @click="editor.chain().focus().toggleBold().run()">
						<Icon :icon="icons.bold"/>
					</btn>

					<btn icon small color="black" :value="editor.isActive('italic')" @click="editor.chain().focus().toggleItalic().run()">
						<Icon :icon="icons.italic"/>
					</btn>

					<btn icon small color="black" :value="editor.isActive('underline')" @click="editor.chain().focus().toggleUnderline().run()">
						<Icon :icon="icons.underline"/>
					</btn>

					<btn icon small color="black" :value="editor.isActive('strike')" @click="editor.chain().focus().toggleStrike().run()">
						<Icon :icon="icons.strike"/>
					</btn>

					<btn v-if="editor.isActive('highlight') || !!editor.getAttributes('textStyle').color" small icon :value="true"
						 @click="editor.chain().focus().unsetColor().unsetHighlight().run()">
						<Icon :icon="icons.color_highlight_cancel" :color="editor.getAttributes('highlight').color"/>
					</btn>

					<btn v-if="!editor.isActive('paragraph')" icon small color="black" :value="true" @click="editor.chain().focus().clearNodes().unsetAllMarks().run()">
						<Icon :icon="icons.clear_format"/>
					</btn>
				</template>
			</bubble-menu>

			<div class="flex flex-column w-full" :class="{'mb-80': props.plain, 'h-full': isFullScreen}">
				<div v-if="editor" class="w-full flex p-8 border-box items-center justify-between"
					 :class="[props.plain ? 'border-t absolute bottom-0 left-0' : 'border-b', {'fixed top-0': isFullScreen}]">
					<div class="flex items-center flex-wrap">
						<div v-if="!props.noHeadings" class="flex items-center">
							<btn text tabindex="-1" small icon :value="editor.isActive('paragraph')" @click="editor.chain().focus().setParagraph().run()">
								<Icon :icon="icons.paragraph"/>
							</btn>

							<btn text tabindex="-1" small icon :value="editor.isActive('heading', { level: 2 })" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()">
								H1
							</btn>

							<btn text tabindex="-1" small icon :value="editor.isActive('heading', { level: 3 })" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">
								H2
							</btn>

							<btn text tabindex="-1" small icon :value="editor.isActive('heading', { level: 4 })" @click="editor.chain().focus().toggleHeading({ level: 4 }).run()">
								H3
							</btn>

							<divider vertical margin="4"/>
						</div>

						<div class="flex items-center">
							<btn icon small text tabindex="-1" :value="editor.isActive('bold')" @click="editor.chain().focus().toggleBold().run()">
								<Icon :icon="icons.bold"/>
							</btn>

							<btn icon small text tabindex="-1" :value="editor.isActive('italic')" @click="editor.chain().focus().toggleItalic().run()">
								<Icon :icon="icons.italic"/>
							</btn>

							<btn icon small text tabindex="-1" :value="editor.isActive('underline')" @click="editor.chain().focus().toggleUnderline().run()">
								<Icon :icon="icons.underline"/>
							</btn>

							<btn icon small text tabindex="-1" :value="editor.isActive('strike')" @click="editor.chain().focus().toggleStrike().run()">
								<Icon :icon="icons.strike"/>
							</btn>

							<divider vertical margin="4"/>
						</div>

						<div class="flex items-center">
							<btn icon small text tabindex="-1" :value="editor.isActive({ textAlign: 'left' })" @click="editor.chain().focus().setTextAlign('left').run()">
								<Icon :icon="icons.align_left"/>
							</btn>

							<btn icon small text tabindex="-1" :value="editor.isActive({ textAlign: 'center' })" @click="editor.chain().focus().setTextAlign('center').run()">
								<Icon :icon="icons.align_center"/>
							</btn>

							<btn icon small text tabindex="-1" :value="editor.isActive({ textAlign: 'right' })" @click="editor.chain().focus().setTextAlign('right').run()">
								<Icon :icon="icons.align_right"/>
							</btn>

							<divider vertical margin="4"/>
						</div>

						<div class="flex items-center">
							<btn text tabindex="-1" small icon :value="!!editor.getAttributes('textStyle').color" @click="colorInputElement.click()">
								<Icon :icon="icons.color_text"/>
								<span class="form-codearea--color-bar"
									  :style="{backgroundColor: editor.getAttributes('textStyle').color ? editor.getAttributes('textStyle').color : '#008200'}"></span>
							</btn>
							<input tabindex="-1"
								   ref="colorInputElement"
								   class="form-codearea--color-picker"
								   type="color"
								   @input="event => editor.chain().focus().setColor(event.target.value).run()"
								   :value="editor.getAttributes('textStyle').color">

							<btn text tabindex="-1" small icon :value="editor.isActive('highlight')" @click="highlightInputElement.click()">
								<Icon :icon="icons.color_highlight"/>
								<span class="form-codearea--color-bar"
									  :style="{backgroundColor: editor.getAttributes('highlight').color ? editor.getAttributes('highlight').color : '#EB9E05'}"></span>
							</btn>
							<input tabindex="-1"
								   ref="highlightInputElement"
								   class="form-codearea--color-picker"
								   type="color"
								   @input="event => editor.chain().focus().setHighlight({color: event.target.value}).run()"
								   :value="editor.getAttributes('highlight').color">

							<divider vertical margin="4"/>
						</div>

						<div class="flex items-center">
							<btn text tabindex="-1" small icon :value="editor.isActive('bulletList')" @click="editor.chain().focus().toggleBulletList().run()">
								<Icon :icon="icons.bullet_list"/>
							</btn>

							<btn text tabindex="-1" small icon :value="editor.isActive('orderedList')" @click="editor.chain().focus().toggleOrderedList().run()">
								<Icon :icon="icons.ordered_list"/>
							</btn>

							<btn v-if="!props.noMedia" small tabindex="-1" text icon @click="showImageModal()">
								<Icon :icon="icons.image"/>
							</btn>

							<btn small text tabindex="-1" icon @click="editor.chain().focus().insertTable({rows: 2, cols: 2, withHeaderRow: true }).run()">
								<Icon :icon="icons.table"/>
							</btn>

							<template v-if="editor.isActive('table')">
								<divider vertical margin="4"/>

								<btn small text tabindex="-1" icon @click="editor.chain().focus().deleteTable().run()">
									<Icon :icon="icons.table_remove"/>
								</btn>
								<btn small text tabindex="-1" icon @click="editor.chain().focus().addColumnBefore().run()">
									<Icon :icon="icons.table_column_before"/>
								</btn>
								<btn small text tabindex="-1" icon @click="editor.chain().focus().addColumnAfter().run()">
									<Icon :icon="icons.table_column_after"/>
								</btn>
								<btn small text tabindex="-1" icon @click="editor.chain().focus().deleteColumn().run()">
									<Icon :icon="icons.table_column_remove"/>
								</btn>
								<btn small text tabindex="-1" icon @click="editor.chain().focus().addRowAfter().run()">
									<Icon :icon="icons.table_row_after"/>
								</btn>
								<btn small text tabindex="-1" icon @click="editor.chain().focus().addRowBefore().run()">
									<Icon :icon="icons.table_row_before"/>
								</btn>
								<btn small text tabindex="-1" icon @click="editor.chain().focus().deleteRow().run()">
									<Icon :icon="icons.table_row_remove"/>
								</btn>
								<divider vertical margin="4"/>

							</template>
						</div>

						<btn text small tabindex="-1" icon @click="editor.chain().focus().setHorizontalRule().run()">
							<Icon :icon="icons.horizontal_rule"/>
						</btn>
					</div>

					<div class="flex items-center">
						<slot name="buttons_right"/>

						<tooltip v-if="props.generateAction" :text="t('action.auto_generate')">
							<form-btn text small tabindex="-1" icon :action="props.generateAction" method="POST"
									  @success="handleGeneratedContent"
									  :confirm="{title: t('confirm.generate_content.title'), message: t('confirm.generate_content.message'), type: 'info'}">
								<Icon :icon="icons.generate_content"/>
							</form-btn>
						</tooltip>

						<btn v-if="isFullScreen" text small tabindex="-1" icon @click="isFullScreen=false">
							<Icon :icon="icons.minimize_screen"/>
						</btn>
						<btn v-else text small tabindex="-1" icon @click="isFullScreen=true">
							<Icon :icon="icons.maximize_screen"/>
						</btn>
						<tooltip v-if="props.help" :text="props.help" left with-touch class="ml-8">
							<Icon :icon="icons.help" size="18" color="gray-400"/>
						</tooltip>
					</div>
				</div>
				<div class="form-input__field form-input__code-field items-start"
					 :style="{minHeight: isFullScreen ? '90%' : minHeightStyle, margin: isFullScreen ? '64px auto 32px' : null}" @click.self="setCursor">
					<loader v-if="isLoading" height="100%" width="100%" class="absolute left-0 right-0 bottom-0 top-0 bg-white opacity-75 z-1"/>

					<editor-content ref="inputElement" :editor="editor" class="w-full"/>
				</div>
			</div>
		</div>
		<div v-if="!props.hideDetails" class="form-input__details" :class="{'fixed bottom-0': isFullScreen}">
			<span v-if="hasError && errorMessage" class="form-input__message" tabindex="-1">{{ errorMessage }}</span>
		</div>

		<lazy-image-upload-modal v-if="!noMedia" v-model="isImageModalVisible" :disk="props.uploadDisk" :path="props.uploadPath" @uploaded="handleMediaUploaded"/>

	</div>
</template>

<script setup lang="ts">
import type {Ref} from "vue";
import {computed, ref, watch, onBeforeUnmount, shallowRef} from "vue";
import {useNuxtApp, useI18n} from "#imports";
import {BubbleMenu, EditorContent, Editor} from '@tiptap/vue-3'
import type {AnyExtension} from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextStyle from '@tiptap/extension-text-style'
import {Color} from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Image from "../../../extensions/tiptap/Image";
import {
	mdiAutoFix,
	mdiClose,
	mdiCursorMove,
	mdiFormatAlignCenter,
	mdiFormatAlignLeft,
	mdiFormatAlignRight,
	mdiFormatBold,
	mdiFormatClear,
	mdiFormatColorHighlight,
	mdiFormatColorMarkerCancel,
	mdiFormatColorText,
	mdiFormatItalic,
	mdiFormatListBulleted,
	mdiFormatListNumbered,
	mdiFormatParagraph,
	mdiFormatStrikethrough,
	mdiFormatUnderline,
	mdiLinkOff,
	mdiLinkVariant,
	mdiMinus,
	mdiMonitorScreenshot,
	mdiRedo,
	mdiTable,
	mdiTableColumnPlusAfter,
	mdiTableColumnPlusBefore,
	mdiTableColumnRemove,
	mdiTableMergeCells,
	mdiTableRemove,
	mdiTableRowPlusAfter,
	mdiTableRowPlusBefore,
	mdiTableRowRemove,
	mdiUndo
} from "@mdi/js";

import {formatDimension, stripHtml} from "@spoferan/spoferan-ts-core";
import {TextAlign} from "@tiptap/extension-text-align";
import {backIcon, helpIcon, imageIcon, saveIcon} from "../../../config";
import {TextInputComponentEmits, type TextInputComponentProps, useTextInputComponent} from "../../../composables/useTextInputComponent";
import {FullScreenComponentEmits, type FullScreenComponentProps, useFullScreenComponent} from "../../../composables/useFullScreenComponent";

const props = withDefaults(defineProps<{
	// The storage path where to upload file content
	uploadPath?: string,

	// The storage disk where to upload file content
	uploadDisk?: string,

	// Whether to disable the various heading styles
	noHeadings?: boolean,

	// Whether to disable the insert of media files
	noMedia?: boolean,

	minHeight?: string | number,

	// A URL to an API EP that can automatically generate the content
	generateAction?: string,
} & TextInputComponentProps & FullScreenComponentProps>(), {
	uploadPath: 'uploads',
	uploadDisk: '',
	noHeadings: false,
	noMedia: false,
	minHeight: 120 // 3 lines
});

const emit = defineEmits([...TextInputComponentEmits, ...FullScreenComponentEmits]);

const icons = {
	link: mdiLinkVariant,
	unlink: mdiLinkOff,
	move: mdiCursorMove,
	bold: mdiFormatBold,
	italic: mdiFormatItalic,
	undo: mdiUndo,
	redo: mdiRedo,
	back: backIcon,
	save: saveIcon,
	paragraph: mdiFormatParagraph,
	underline: mdiFormatUnderline,
	strike: mdiFormatStrikethrough,
	bullet_list: mdiFormatListBulleted,
	ordered_list: mdiFormatListNumbered,
	horizontal_rule: mdiMinus,
	image: imageIcon,
	color_text: mdiFormatColorText,
	color_highlight: mdiFormatColorHighlight,
	color_highlight_cancel: mdiFormatColorMarkerCancel,
	table: mdiTable,
	table_remove: mdiTableRemove,
	table_column_after: mdiTableColumnPlusAfter,
	table_column_before: mdiTableColumnPlusBefore,
	table_column_remove: mdiTableColumnRemove,
	table_row_after: mdiTableRowPlusAfter,
	table_row_before: mdiTableRowPlusBefore,
	table_row_remove: mdiTableRowRemove,
	table_merge_cells: mdiTableMergeCells,
	maximize_screen: mdiMonitorScreenshot,
	minimize_screen: mdiClose,
	clear_format: mdiFormatClear,
	align_left: mdiFormatAlignLeft,
	align_center: mdiFormatAlignCenter,
	align_right: mdiFormatAlignRight,
	help: helpIcon,
	generate_content: mdiAutoFix
}

const {$apiFetch} = useNuxtApp();
const {t} = useI18n();

const inputElement: Ref<HTMLElement | null> = ref(null);
const wrapperElement: Ref<HTMLElement | null> = ref(null);
const colorInputElement: Ref<HTMLElement | null> = ref(null);
const highlightInputElement: Ref<HTMLElement | null> = ref(null);

const {submitValue, isActive, handleFocus, handleBlur, isRequired, hasSuffix, hasPrefix, hasError, errorMessage} = useTextInputComponent(inputElement, props, emit, {
	validationValue: computed(() => props.modelValue ? stripHtml(props.modelValue) : null)
});
const {isFullScreen} = useFullScreenComponent(wrapperElement, props, emit);

const isImageModalVisible = ref(false);
const linkUrl = ref(null);
const linkMenuVisible = ref(false);
const isLoading = ref(false);

const minHeightStyle = computed(() => {
	return props.minHeight ? formatDimension(props.minHeight) : null;
});

const editor = shallowRef(new Editor({
	editable: !props.disabled,
	autofocus: props.autofocus,
	enableInputRules: false, // disable markdown when typing
	extensions: [
		StarterKit.configure({
			heading: props.noHeadings ? false : {levels: [2, 3, 4]}
		}),
		Link.configure({
			autolink: true,
			linkOnPaste: true,
			openOnClick: false,
		}),
		Underline,
		Table.configure({
			resizable: true,
		}),
		TableHeader,
		TableCell,
		TableRow,
		Highlight.configure({
			multicolor: true,
		}),
		Color,
		TextStyle, // Required for color
		TextAlign.configure({
			alignments: ['left', 'center', 'right'],
			types: ['heading', 'paragraph'],
		}),
		props.placeholder ? Placeholder.configure({
			showOnlyWhenEditable: true,
			showOnlyCurrent: true,
			placeholder: props.placeholder,
		}) : undefined,
		!props.noMedia ? Image.configure({
			uploadCallback: uploadImage
		}) : undefined
	].filter(Boolean) as AnyExtension[],
	content: props.modelValue,
	onUpdate: () => {
		const htmlContent = editor.value.getHTML();
		submitValue.value = htmlContent === '<p></p>' ? null : htmlContent;
	},
	onFocus: handleFocus,
	onBlur: handleBlur,
}));

watch(() => props.modelValue, (newValue) => {
	const isSame = editor.value.getHTML() === newValue
	if (isSame) {
		return
	}

	editor.value.commands.setContent(newValue, false)
});

watch(() => props.disabled, (newValue) => {
	if (editor.value) {
		editor.value.setOptions({editable: !newValue})
	}
});

function setCursor() {
	if (!isActive.value && editor.value) {
		editor.value.commands.focus('end');
	}
}

function showLinkMenu(attrs) {
	linkUrl.value = attrs.href
	linkMenuVisible.value = true
}

function hideLinkMenu() {
	linkUrl.value = null
	linkMenuVisible.value = false
}

function setLinkUrl(url) {
	if (url) {
		// We want to open all external links in a new target
		const target = url && url.includes('spoferan.com') ? null : '_blank';
		editor.value?.commands.setLink({href: url, target: target})
	} else {
		editor.value?.commands.unsetLink()
	}

	hideLinkMenu()
}

function showImageModal() {
	if (props.disabled) {
		return;
	}
	isImageModalVisible.value = true;
}

function handleMediaUploaded(media) {
	editor.value?.commands.setImage({src: media.url, ratio: media.ratio, path: media.path});
}

function uploadImage(imageFile) {
	if (props.disabled) {
		return;
	}
	if (imageFile) {
		return new Promise(((resolve, reject) => {
			$apiFetch('/v1/media/files', {
				params: {
					file: imageFile,
					disk: props.uploadDisk,
					path: props.uploadPath
				},
				method: 'post'
			}).then(response => {
				resolve({src: response.data.url, ratio: response.data.ratio, path: response.data.path});
			}).catch(error => {
				reject(error);
			})
		}))
	}
}

function handleGeneratedContent(data) {
	submitValue.value = data.text
}

onBeforeUnmount(() => {
	editor.value?.destroy();
});

</script>

<style>
@charset "UTF-8";
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
/* ==========================================================================
   Mixins
   ========================================================================== */
.form-input {
  width: 100%;
  position: relative;
}
.form-input .form-input__content {
  width: 100%;
  min-height: 38px;
  box-sizing: border-box;
  border: 1px solid #d8d8d8;
  border-radius: 5px;
  background-color: #ffffff;
  display: flex;
  position: relative;
  align-items: center;
}
.form-input .form-input__content > label, .form-input .form-input__content > .form-input__field-wrapper, .form-input .form-input__content > .dropdown, .form-input .form-input__content > .dropdown > .dropdown__activator {
  height: 100%;
  width: 100%;
  min-height: inherit;
  display: flex;
  align-items: center;
  position: relative;
}
.form-input .form-input__content .form-input__label {
  position: absolute;
  font-size: 15px;
  top: 10px;
  line-height: 1.2;
  left: 4px;
  background: #ffffff;
  color: #5e6b6b;
  padding: 0 4px;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  transform-origin: top left;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.form-input .form-input__content .form-input__field {
  width: 100%;
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1.2;
  min-height: 38px;
  padding: 4px 8px;
  border: none;
  outline: none;
  color: #525e5e;
  background-color: #ffffff;
  border-radius: 5px;
  display: flex;
  align-items: center;
}
.form-input .form-input__content .form-input__field .form-input__placeholder {
  color: #7D8484;
}
.form-input .form-input__content .form-input__field::-webkit-input-placeholder { /* Chrome/Opera/Safari */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field::-moz-placeholder { /* Firefox 19+ */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field:-ms-input-placeholder { /* IE 10+ */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field:-moz-placeholder { /* Firefox 18- */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field[type=search] {
  -webkit-appearance: none;
}
.form-input .form-input__content .form-input__field::-webkit-search-cancel-button {
  display: none;
}
.form-input .form-input__content .form-input__field-content {
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1.2;
  color: #525e5e;
}
.form-input .form-input__details {
  display: flex;
  width: 100%;
  height: 16px;
  margin-bottom: 8px;
  position: relative;
}
.form-input .form-input__details > .form-input__message {
  font-size: 11px;
  line-height: 1.1;
  text-align: left;
  font-weight: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 1px 8px;
}
.form-input .form-input__details > .form-input__message:focus, .form-input .form-input__details > .form-input__message:hover {
  overflow: unset;
  text-overflow: unset;
  white-space: unset;
  position: absolute;
  z-index: 1;
  background: #ffffff;
}
.form-input.is-active .form-input__content {
  border-color: #008200;
}
.form-input.is-required .form-input__label:after {
  content: " *";
  color: #FA5555;
}
.form-input.is-disabled {
  opacity: 0.8;
}
.form-input.is-disabled .form-input__field {
  cursor: not-allowed;
}
.form-input:not(.is-empty) .form-input__content .form-input__label, .form-input.is-active .form-input__content .form-input__label {
  left: 4px !important;
  transform: translateY(-18px) scale(0.7);
  max-width: 120%;
}
.form-input:not(.is-empty).has-suffix .form-input__content .form-input__label, .form-input.is-active.has-suffix .form-input__content .form-input__label {
  padding-right: 0;
}
.form-input.has-success .form-input__content {
  border-color: #47a447 !important;
}
.form-input.has-success .form-input__message {
  color: #47a447;
}
.form-input.has-success .form-input__label {
  color: #47a447;
}
.form-input.has-error .form-input__content {
  border-color: #FA5555 !important;
}
.form-input.has-error .form-input__message {
  color: #FA5555;
}
.form-input.has-error .form-input__label {
  color: #FA5555;
}
.form-input.has-warning .form-input__content {
  border-color: #EB9E05 !important;
}
.form-input.has-warning .form-input__message {
  color: #EB9E05;
}
.form-input.has-warning .form-input__label {
  color: #EB9E05;
}
.form-input.has-prefix .form-input__content .form-input__field-wrapper {
  padding-left: 32px;
}
.form-input.has-prefix .form-input__content .form-input__label {
  left: 32px;
}
.form-input.has-prefix .form-input__prefix {
  position: absolute;
  top: 20px;
  transform: translateY(-50%);
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
.form-input.has-prefix .form-input__prefix .icon, .form-input.has-prefix .form-input__prefix .icon > svg {
  width: 18px;
  height: 18px;
}
.form-input.has-suffix .form-input__content .form-input__field-wrapper, .form-input.has-suffix .form-input__content .form-input__label {
  padding-right: 32px;
}
.form-input.has-suffix .form-input__suffix {
  position: absolute;
  top: 20px;
  transform: translateY(-50%);
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
.form-input.has-suffix .form-input__suffix .icon, .form-input.has-suffix .form-input__suffix .icon > svg {
  width: 18px;
  height: 18px;
  color: #7D8484;
}
.form-input.is-inverted .form-input__content {
  border-color: #f4f4f5;
  background-color: #f4f4f5;
}
.form-input.is-inverted .form-input__content .form-input__field {
  color: #525e5e;
  background-color: #f4f4f5;
}
.form-input.is-inverted .form-input__content .form-input__field .form-input__placeholder {
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field::-webkit-input-placeholder { /* Chrome/Opera/Safari */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field::-moz-placeholder { /* Firefox 19+ */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field:-ms-input-placeholder { /* IE 10+ */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field:-moz-placeholder { /* Firefox 18- */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__label {
  background: transparent;
}
.form-input.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.is-plain .form-input__content {
  border: none;
}
.form-input.is-plain .form-input__content .form-input__field {
  padding: 0 !important;
}
.form-input.is-plain .form-input__details .form-input__message {
  padding-left: 0;
  padding-right: 0;
}
.form-input.form-input-xs .form-input__content {
  min-height: 24px;
}
.form-input.form-input-xs .form-input__content .form-input__label {
  font-size: 11.5px;
  top: 5.1px;
}
.form-input.form-input-xs .form-input__content .form-input__field {
  min-height: 24px;
  font-size: 11.5px;
}
.form-input.form-input-xs .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-xs.has-prefix .form-input__prefix {
  top: 13px;
}
.form-input.form-input-xs.has-suffix .form-input__suffix {
  top: 13px;
}
.form-input.form-input-xs:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xs.is-active .form-input__content .form-input__label {
  transform: translateY(-11px) scale(0.7);
}
.form-input.form-input-xs.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xs.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-17px) scale(0.7);
}
.form-input.form-input-sm .form-input__content {
  min-height: 32px;
}
.form-input.form-input-sm .form-input__content .form-input__label {
  font-size: 12px;
  top: 8.8px;
}
.form-input.form-input-sm .form-input__content .form-input__field {
  min-height: 32px;
  font-size: 12px;
}
.form-input.form-input-sm .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-sm.has-prefix .form-input__prefix {
  top: 17px;
}
.form-input.form-input-sm.has-suffix .form-input__suffix {
  top: 17px;
}
.form-input.form-input-sm:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-sm.is-active .form-input__content .form-input__label {
  transform: translateY(-15px) scale(0.7);
}
.form-input.form-input-sm.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-sm.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-21px) scale(0.7);
}
.form-input.form-input-md .form-input__content {
  min-height: 38px;
}
.form-input.form-input-md .form-input__content .form-input__label {
  font-size: 15px;
  top: 10px;
}
.form-input.form-input-md .form-input__content .form-input__field {
  min-height: 38px;
  font-size: 15px;
}
.form-input.form-input-md .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-md.has-prefix .form-input__prefix {
  top: 20px;
}
.form-input.form-input-md.has-suffix .form-input__suffix {
  top: 20px;
}
.form-input.form-input-md:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-md.is-active .form-input__content .form-input__label {
  transform: translateY(-18px) scale(0.7);
}
.form-input.form-input-md.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-md.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.form-input-lg .form-input__content {
  min-height: 46px;
}
.form-input.form-input-lg .form-input__content .form-input__label {
  font-size: 15px;
  top: 14px;
}
.form-input.form-input-lg .form-input__content .form-input__field {
  min-height: 46px;
  font-size: 15px;
}
.form-input.form-input-lg .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-lg.has-prefix .form-input__prefix {
  top: 24px;
}
.form-input.form-input-lg.has-suffix .form-input__suffix {
  top: 24px;
}
.form-input.form-input-lg:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-lg.is-active .form-input__content .form-input__label {
  transform: translateY(-22px) scale(0.7);
}
.form-input.form-input-lg.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-lg.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-28px) scale(0.7);
}
.form-input.form-input-xl .form-input__content {
  min-height: 50px;
}
.form-input.form-input-xl .form-input__content .form-input__label {
  font-size: 16px;
  top: 15.4px;
}
.form-input.form-input-xl .form-input__content .form-input__field {
  min-height: 50px;
  font-size: 16px;
}
.form-input.form-input-xl .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-xl.has-prefix .form-input__prefix {
  top: 26px;
}
.form-input.form-input-xl.has-suffix .form-input__suffix {
  top: 26px;
}
.form-input.form-input-xl:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xl.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.form-input-xl.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xl.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-30px) scale(0.7);
}

/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
.form-input.form-codearea.is-plain > .form-input__content {
  border: none;
}
.form-input.form-codearea.is-plain > .form-input__content .form-input__field.form-input__code-field {
  max-height: unset;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field {
  box-sizing: border-box;
  overflow-y: auto;
  resize: vertical;
  height: auto;
  cursor: text;
  text-align: left;
  max-width: 950px;
  color: #465050;
  font-size: 15px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field h2, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field h3, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field h4, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field h5, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field h6 {
  font-weight: 600;
  margin-bottom: 1rem;
  line-height: 1.3;
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field h2 {
  font-size: 20px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field h3 {
  font-size: 18px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field h4 {
  font-size: 16px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field p {
  margin-bottom: 1rem;
  line-height: 1.5;
  font-size: 15px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field p:last-child {
  margin-bottom: 0;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ul {
  list-style: disc inside;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ol {
  list-style: decimal inside;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ul, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ol {
  margin-bottom: 16px;
  margin-top: 0;
  padding-left: 40px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ul li, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ol li {
  padding: 4px 0;
  line-height: 1.3;
  list-style-position: outside;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ul li > p, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ol li > p {
  display: inline;
  line-height: 1.3;
  font-size: 14.6px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ul li > p > img:only-child, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ol li > p > img:only-child {
  display: block;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ul li ul, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ol li ul {
  margin-top: 4px;
  margin-bottom: 0;
  padding-left: 24px;
}
@media (max-width: 767px) {
  .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ul, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field ol {
    padding-left: 24px;
  }
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field hr {
  height: 1px;
  border: 0;
  width: 100%;
  text-align: center;
  margin: 16px auto;
  background-color: #d8d8d8;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field blockquote {
  position: relative;
  padding-left: 1em;
  border-left: 3px solid #e6ecf5;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field blockquote:before, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field blockquote:after {
  content: "“";
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field blockquote:after {
  content: "”";
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-1 {
  margin-left: 25px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-2 {
  margin-left: 50px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-3 {
  margin-left: 75px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-4 {
  margin-left: 100px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-5 {
  margin-left: 125px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-6 {
  margin-left: 150px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-7 {
  margin-left: 175px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-8 {
  margin-left: 200px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-9 {
  margin-left: 225px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ql-indent-10 {
  margin-left: 250px;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field table {
  border-spacing: 0;
  border-color: #d8d8d8;
  border-collapse: collapse;
  table-layout: fixed;
  overflow: hidden;
  width: 100%;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field table td, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field table th {
  padding: 8px;
  border: 2px solid #d8d8d8;
  color: #465050;
  vertical-align: top;
  box-sizing: border-box;
  text-align: left;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field table td p, .form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field table th p {
  margin-bottom: 0;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field img {
  border-radius: 5px;
  max-height: 400px;
  max-width: 100%;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field div > img {
  margin: 16px 0;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field a:not(.btn):hover {
  text-decoration: underline;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field iframe {
  max-width: 100%;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ProseMirror {
  outline: 0;
}
.form-input.form-codearea > .form-input__content .form-input__field.form-input__code-field .ProseMirror p:last-child {
  margin-bottom: 0;
}
.form-input.form-codearea .ProseMirror *.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #7D8484;
  pointer-events: none;
  height: 0;
}
.form-input.form-codearea .form-codearea--color-picker {
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
  border: none;
}
.form-input.form-codearea .form-codearea--color-bar {
  position: absolute;
  margin-left: 0 !important;
  height: 3px;
  bottom: 6px;
  width: 14px;
}

.form-input-menu-bubble {
  z-index: 20;
  background: #2e3535;
  border-radius: 5px;
  padding: 0.3rem;
}
</style>